import type { AssignedPerspectives } from 'types/global';
import type { Schema } from 'types/schema';
import { extractSchemaDefaults } from '@center-platform/spheres/sphere';

type FormProperties = Record<string, unknown>;

/** Structural equality over JSON values, which is all a property default can be. */
const sameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aKeys = Object.keys(a);
  const bRecord = b as Record<string, unknown>;
  return (
    aKeys.length === Object.keys(b).length &&
    aKeys.every((key) => key in bRecord && sameValue((a as Record<string, unknown>)[key], bRecord[key]))
  );
};

/** The form value that mirrors a perspective's assignment: the first assigned sphere's id, or '' when none. */
export const perspectiveFieldValue = (
  assignedPerspectives: AssignedPerspectives | undefined,
  perspectiveSystemName: string,
): string =>
  assignedPerspectives?.find((ap) => ap?.perspectiveProperties?.system_name === perspectiveSystemName)?.assigned?.[0]?.sphere
    ?.id ?? '';

const requiredPerspectiveNames = (schema: Schema | null): string[] =>
  (schema?.perspectives ?? []).filter((perspective) => perspective.required).map((perspective) => perspective.system_name);

/** One form field per perspective `schema` requires, holding its current assignment (see {@link perspectiveFieldValue}). */
export const requiredPerspectiveFields = (
  schema: Schema | null,
  assignedPerspectives: AssignedPerspectives | undefined,
): Record<string, string> =>
  Object.fromEntries(requiredPerspectiveNames(schema).map((name) => [name, perspectiveFieldValue(assignedPerspectives, name)]));

/**
 * The form's `properties` after a new sphere switches from type `from` to type `to`: what
 * `initializeSphere` gives a sphere of `to`, plus every value the person entered.
 *
 * - `type` becomes `to`'s system name.
 * - A field still holding `from`'s default (or no value) takes `to`'s default; it is dropped when
 *   `to` has no default for it.
 * - Each required perspective's field follows `to`: added from the current assignment, dropped when
 *   `to` does not require it.
 * - A value the person entered is never overwritten or dropped, even for a field `to` lacks.
 *
 * `from` is null when the previous type never loaded; then only empty fields take `to`'s defaults.
 */
export function retypeFormProperties(
  current: FormProperties,
  from: Schema | null,
  to: Schema,
  assignedPerspectives: AssignedPerspectives | undefined,
): FormProperties {
  const fromDefaults: FormProperties = from ? extractSchemaDefaults(from) : {};
  const toDefaults: FormProperties = extractSchemaDefaults(to);
  const next: FormProperties = { ...current };

  const holdsFromDefault = (name: string) =>
    next[name] === undefined || (name in fromDefaults && sameValue(next[name], fromDefaults[name]));

  for (const name of new Set([...Object.keys(fromDefaults), ...Object.keys(toDefaults)])) {
    if (!holdsFromDefault(name)) continue;
    if (name in toDefaults) next[name] = toDefaults[name];
    else delete next[name];
  }

  const toRequired = new Set(requiredPerspectiveNames(to));
  for (const name of requiredPerspectiveNames(from)) {
    if (!toRequired.has(name)) delete next[name];
  }
  for (const name of toRequired) {
    if (!next[name]) next[name] = perspectiveFieldValue(assignedPerspectives, name);
  }

  next['type'] = to.system_name;
  return next;
}
